/**
 * The re-plan calendar — a flights-style range picker over ONE month. The
 * current plan is the amber band, the data date is marked, weekends and days
 * at/behind the data date are off. Block mode: first click sets the new start
 * (the finish follows, duration held), second click sets the finish (a
 * duration change); a click behind the start restarts the pick. Finish mode
 * (started rows) and point mode (milestones) pick a single date. The footer
 * carries the same slide ◀ ▶ / duration − + the row offers, plus Reset/Done.
 * Hosted in the shared Popover (portaled, flips above near the viewport
 * bottom, outside-click + Escape close).
 */
import React from "react";
import Popover from "../../../../shared/Popover/Popover";
import { formatDateMonthDay } from "../../../../../utils/format";
import { isWeekend, shiftWorkdays, stepWorkday, workdaysBetween } from "../../dates";
import type { PlanMode, StagedPlan } from "../../plan";
import "./PlanRangePicker.css";

interface Props {
  code: string;
  name: string;
  mode: Exclude<PlanMode, "none">;
  /** Effective (staged or committed) plan being edited. */
  plan: StagedPlan;
  /** Committed actual start shown in finish mode (iso10), if any. */
  lockedStart: string;
  dataDate: string;
  moved: boolean;
  /** The PlanCell span button the picker anchors beneath. */
  triggerRef: React.RefObject<HTMLElement | null>;
  onChange: (plan: StagedPlan) => void;
  onSlide: (dir: 1 | -1) => void;
  onReset: () => void;
  onClose: () => void;
}

const monthOf = (iso: string): string => iso.slice(0, 7);

const stepMonth = (ym: string, dir: 1 | -1): string => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + dir, 1));
  return d.toISOString().slice(0, 7);
};

const PlanRangePicker: React.FC<Props> = ({
  code,
  name,
  mode,
  plan,
  lockedStart,
  dataDate,
  moved,
  triggerRef,
  onChange,
  onSlide,
  onReset,
  onClose,
}) => {
  const [month, setMonth] = React.useState(() =>
    monthOf(plan.start > dataDate ? plan.start : dataDate));
  // Block mode is a two-click pick: after the first click (new start) the next
  // in-range click sets the finish instead of restarting.
  const [pendingFinish, setPendingFinish] = React.useState(false);

  const pick = (iso: string) => {
    if (mode === "point") {
      onChange({ start: iso, finish: iso });
      return;
    }
    if (mode === "finish") {
      onChange({ start: plan.start, finish: iso });
      return;
    }
    if (!pendingFinish || iso < plan.start) {
      const days = workdaysBetween(plan.start, plan.finish);
      onChange({ start: iso, finish: shiftWorkdays(iso, days - 1) });
      setPendingFinish(true);
    } else {
      onChange({ start: plan.start, finish: iso });
      setPendingFinish(false);
    }
  };

  const stretch = (dir: 1 | -1) => {
    const nf = stepWorkday(plan.finish, dir);
    if (dir < 0 && (nf < plan.start || nf <= dataDate)) return;
    onChange({ start: plan.start, finish: nf });
  };

  const bandStart = mode === "finish" ? (lockedStart || plan.start) : plan.start;

  const renderMonth = (ym: string) => {
    const [y, m] = ym.split("-").map(Number);
    const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells: React.ReactNode[] = [];
    ["S", "M", "T", "W", "T", "F", "S"].forEach((d, i) =>
      cells.push(<div key={`dow${i}`} className="rp-dow">{d}</div>));
    for (let i = 0; i < firstDow; i++) {
      cells.push(<div key={`b${i}`} className="rp-day rp-blank" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${ym}-${String(day).padStart(2, "0")}`;
      const disabled = iso <= dataDate || isWeekend(iso);
      const isStart = mode !== "finish" && iso === plan.start;
      const isEnd = iso === plan.finish;
      const inBand = iso > bandStart && iso < plan.finish && !isWeekend(iso);
      const cls = [
        "rp-day",
        disabled ? "rp-dis" : "",
        iso === dataDate ? "rp-dd" : "",
        isStart || isEnd ? "rp-pick" : "",
        inBand ? "rp-in" : "",
      ].filter(Boolean).join(" ");
      cells.push(
        <div
          key={iso}
          className={cls}
          onClick={disabled ? undefined : () => pick(iso)}
        >
          {day}
        </div>,
      );
    }
    return <div className="rp-grid">{cells}</div>;
  };

  const monthTitle = new Date(month + "-01T00:00:00Z")
    .toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const msg = mode === "point"
    ? "Pick the new date"
    : mode === "finish"
      ? "Pick the new finish — the start already happened"
      : pendingFinish
        ? "Now pick the finish — or slide / Done"
        : "Pick the new start — finish follows, duration held";
  const days = workdaysBetween(mode === "finish" ? stepWorkday(dataDate, 1) : plan.start, plan.finish);

  return (
    <Popover open triggerRef={triggerRef} onClose={onClose} className="rp-panel">
      <div className="rp-head">
        <span className="rp-who">{code} · {name}</span>
        <button
          type="button"
          className={`rp-undo${moved ? "" : " rp-ghost"}`}
          title="Back to the planned dates"
          onClick={() => { onReset(); setPendingFinish(false); }}
        >Reset</button>
        <button type="button" className="rp-x" title="Close" onClick={onClose}>✕</button>
      </div>
      <div className="rp-msg">{msg}</div>
      <div className="rp-mtitle">
        <button type="button" className="rp-nav" onClick={() => setMonth((v) => stepMonth(v, -1))}>‹</button>
        <span>{monthTitle}</span>
        <button type="button" className="rp-nav" onClick={() => setMonth((v) => stepMonth(v, 1))}>›</button>
      </div>
      {renderMonth(month)}
      <div className="rp-range">
        {mode === "point" ? (
          <b>{formatDateMonthDay(plan.finish)}</b>
        ) : mode === "finish" ? (
          <>
            <span className="rp-muted">started {formatDateMonthDay(lockedStart || plan.start)}</span>
            <span className="rp-sep">·</span>
            <b>{formatDateMonthDay(plan.finish)}</b>
          </>
        ) : (
          <>
            <b>{formatDateMonthDay(plan.start)}</b>
            <span className="rp-sep">–</span>
            <b>{formatDateMonthDay(plan.finish)}</b>
          </>
        )}
        {mode !== "point" && <span className="rp-dur">· {days} workday{days === 1 ? "" : "s"}</span>}
      </div>
      <div className="rp-foot">
        <span className="rp-lbl">slide</span>
        <button
          type="button"
          title={mode === "finish" ? "Finish 1 workday earlier" : "Whole plan 1 workday earlier"}
          onClick={() => onSlide(-1)}
        >◀</button>
        <button
          type="button"
          title={mode === "finish" ? "Finish 1 workday later" : "Whole plan 1 workday later"}
          onClick={() => onSlide(1)}
        >▶</button>
        {mode === "block" && (
          <>
            <span className="rp-lbl">duration</span>
            <button type="button" title="1 workday shorter" onClick={() => stretch(-1)}>−</button>
            <button type="button" title="1 workday longer" onClick={() => stretch(1)}>+</button>
          </>
        )}
        <button type="button" className="rp-done" onClick={onClose}>Done</button>
      </div>
    </Popover>
  );
};

export default PlanRangePicker;
